import { i18n } from '@lingui/core';
import { I18nProvider } from '@lingui/react';
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';

export const LOCALES = ['en', 'ar'] as const;
export type AppLocale = (typeof LOCALES)[number];

/** Arabic is the only RTL language this product ships. */
const RTL_LOCALES = new Set<AppLocale>(['ar']);

export const isRtl = (locale: AppLocale): boolean => RTL_LOCALES.has(locale);

/**
 * Where the chosen locale is remembered between visits.
 *
 * `localStorage`, and deliberately not a cookie: AGENTS.md §7 forbids keeping
 * *tokens* in localStorage, which this is not — it is a display preference with
 * no authority attached. The server's copy on `User` and `StaffMember` is the
 * real record; this only avoids a flash of English before the profile loads.
 */
const STORAGE_KEY = 'signapass:locale';

export function storedLocale(): AppLocale | null {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    return LOCALES.includes(value as AppLocale) ? (value as AppLocale) : null;
  } catch {
    // Private browsing, or storage disabled. Not worth failing a render over.
    return null;
  }
}

/**
 * Loads a catalogue and activates it.
 *
 * Dynamic import, so Arabic is not in the bundle an English user downloads.
 * AGENTS.md §7 is emphatic about the counter's bundle: "a barista on a cheap
 * Android phone on a slow connection must not download the analytics dashboard
 * bundle", and the same reasoning applies to a language they will never read.
 */
export async function activateLocale(locale: AppLocale): Promise<void> {
  const { messages } = (await import(`../locales/${locale}/messages.po`)) as {
    messages: Record<string, string>;
  };
  i18n.loadAndActivate({ locale, messages });
  applyDocumentLocale(locale);
  try {
    localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    // See above.
  }
}

/**
 * `lang` and `dir` on `<html>` — DESIGN-SYSTEM.md §6.
 *
 * `dir` is what makes every logical CSS property (`ms-*`, `pe-*`, `start-*`)
 * resolve the other way round, so setting it here is what mirrors the entire
 * application rather than a component at a time. `lang` matters separately: it
 * is what a screen reader uses to choose a voice, and what `Intl` picks up.
 */
function applyDocumentLocale(locale: AppLocale): void {
  const root = document.documentElement;
  root.lang = locale;
  root.dir = isRtl(locale) ? 'rtl' : 'ltr';
}

interface LocaleContextValue {
  locale: AppLocale;
  setLocale: (locale: AppLocale) => Promise<void>;
  isRtl: boolean;
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

export function useLocale(): LocaleContextValue {
  const value = useContext(LocaleContext);
  if (!value) throw new Error('useLocale must be used inside <LocaleProvider>');
  return value;
}

/**
 * Activates a locale before rendering children.
 *
 * Renders nothing until the first catalogue has loaded. A brief blank is better
 * than a flash of untranslated message ids, which is what lingui renders before
 * activation — and on the counter, a screen that says `pos.stamp.confirm` for a
 * frame is a screen a barista does not trust.
 */
export function LocaleProvider({
  initial,
  children,
}: {
  initial: AppLocale;
  children: ReactNode;
}) {
  const [locale, setLocaleState] = useState<AppLocale>(initial);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    /**
     * Ready either way.
     *
     * If the catalogue cannot load — offline before it was cached, a corrupted
     * entry, anything — the counter renders in the source locale rather than
     * showing nothing. A barista mid-rush needs a screen in the wrong language
     * far more than a blank one, and AGENTS.md §0.5 does not make an exception
     * for i18n. The catalogues are precached for exactly this reason; this is
     * what happens when that is not enough.
     */
    void activateLocale(initial)
      .catch(() => {
        i18n.loadAndActivate({ locale: 'en', messages: {} });
        applyDocumentLocale('en');
      })
      .finally(() => setReady(true));
  }, [initial]);

  const setLocale = useCallback(async (next: AppLocale) => {
    // Deliberately not caught: a switch the user asked for that silently does
    // nothing is worse than one that surfaces. The initial load above is the
    // path that must never fail closed.
    await activateLocale(next);
    setLocaleState(next);
  }, []);

  if (!ready) return null;

  return (
    <LocaleContext.Provider value={{ locale, setLocale, isRtl: isRtl(locale) }}>
      <I18nProvider i18n={i18n}>{children}</I18nProvider>
    </LocaleContext.Provider>
  );
}
